import { useCallback, useRef, useState } from 'react';
import type { SearchRequest, SearchResponse, SearchResult } from '../lib/types';

interface SearchOptions {
  tier?: string;
  expand?: boolean;
  topK?: number;
}

interface UseSearchState {
  results: SearchResult[];
  loading: boolean;
  error: string | null;
  timingMs: number | null;
  search: (query: string, opts?: SearchOptions) => Promise<void>;
}

export function useSearch(): UseSearchState {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timingMs, setTimingMs] = useState<number | null>(null);
  const activeRequestId = useRef(0);

  const search = useCallback(async (query: string, opts: SearchOptions = {}) => {
    const q = query.trim();
    if (!q) return;

    const requestId = ++activeRequestId.current;
    setLoading(true);
    setError(null);

    const body: SearchRequest = { query: q, top_k: opts.topK ?? 15 };
    if (opts.tier) body.tier = opts.tier;

    const url = opts.expand ? '/api/search?expand=1' : '/api/search';

    // Diagnostic: confirm what the UI is actually sending. Remove once
    // tier filtering is verified end-to-end on live data.
    // eslint-disable-next-line no-console
    console.log('[ryven-wiki] search request', { url, body });

    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (requestId !== activeRequestId.current) return;

      if (!resp.ok) {
        const text = await resp.text();
        setError(`search failed (${resp.status}): ${text.slice(0, 200)}`);
        setResults([]);
        setTimingMs(null);
        return;
      }

      const data = (await resp.json()) as SearchResponse;
      setResults(data.results ?? []);
      setTimingMs(data.timing?.total_ms ?? null);
    } catch (err) {
      if (requestId !== activeRequestId.current) return;
      setError(err instanceof Error ? err.message : 'search failed');
      setResults([]);
      setTimingMs(null);
    } finally {
      if (requestId === activeRequestId.current) setLoading(false);
    }
  }, []);

  return { results, loading, error, timingMs, search };
}
